import { FC, useEffect, useState } from 'react';
import {
  ComponentRendering,
  Field,
  GetStaticComponentProps,
  LayoutServiceData,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { GetStaticPropsContext } from 'next';

import { useSiteSettings } from '@gn-audio/jss-components/utils/contexts';
import { Accessory, getFamilyAccessories, getLocaleWithRegion } from '@gn-audio/jss-core';

import { getSfccProductPrices } from 'api/pricesApi/getSfccProductPrices';
import { StandardPriceModel } from 'api/pricesApi/pricesModel';
import { getSharedProperty, mapSharedPropertiesToFields } from 'common/getters';
import { withTheme } from 'common/jabraTheme/withTheme';
import { parseSCLookupValue } from 'common/siteCoreHelpers';
import SliderWithPrice from 'components/Product/AccessorySpot/SliderWithPrice';
import { ComponentProps } from 'lib/component-props';

export interface ProductCatalogProductWithPrice extends Accessory {
  price: StandardPriceModel;
}

interface AccessorySpotProps extends ComponentProps {
  fields: AccessorySpotFields;
  accessories: Accessory[];
}

type AccessorySpotFields = {
  headline: Field<string>;
};

const AccessorySpot: FC<AccessorySpotProps> = ({ accessories, fields }) => {
  const [prices, setPrices] = useState<StandardPriceModel[]>([]);
  const { ecom } = useSiteSettings();
  const { headline } = mapSharedPropertiesToFields<AccessorySpotFields>(fields);

  useEffect(() => {
    const fetchPrices = async () => {
      if (ecom?.ecomEnabled) {
        const prices = await getSfccProductPrices(accessories.map((accessory) => accessory.sku));
        setPrices(prices);
      } else {
        setPrices(new Array(accessories.length).fill(null));
      }
    };
    void fetchPrices();
  }, [accessories, ecom?.ecomEnabled]);

  if (accessories.length === 0) {
    return null;
  }

  const accessoriesWithPrices = accessories.map((accessory, index) => ({
    ...accessory,
    price: prices[index],
  }));

  return (
    <div className="bg-theme container-fluid overflow-hidden">
      <div className="container">
        <SliderWithPrice header={headline} cards={accessoriesWithPrices} />
      </div>
    </div>
  );
};

const mapAccessoriesWithSku = (accessories: Accessory[]): Accessory[] => {
  return accessories.map((accessory) => ({
    ...accessory,
    pageUrl: `${accessory.pageUrl}?sku=${accessory.sku}`,
  }));
};

export const getStaticProps: GetStaticComponentProps = async (
  rendering: ComponentRendering,
  { sitecore }: LayoutServiceData,
  context: GetStaticPropsContext
) => {
  try {
    const product = (
      getSharedProperty(sitecore.route?.fields, 'Product') as Field<string> | undefined
    )?.value;
    if (!product) {
      return { rendering, accessories: [] };
    }
    const familyLookupCode = parseSCLookupValue(product);
    const language = getLocaleWithRegion(context.locale);
    const accessories = await getFamilyAccessories(+familyLookupCode.id, language);
    return { rendering, accessories: accessories ? mapAccessoriesWithSku(accessories) : [] };
  } catch (error) {
    console.log(error);
    const { params = {} } = rendering;
    return { config: {}, params };
  }
};

export default withTheme({
  defaultTheme: {
    backgroundColor: 'white',
    foregroundColor: 'black',
    darkTheme: false,
  },
})(withDatasourceCheck()<AccessorySpotProps>(AccessorySpot));
